import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send, Minimize2, Bot, Loader2, Mic, MicOff } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '@/hooks/useAuth';
import ReactMarkdown from 'react-markdown';

interface GrantProposal {
  permissionAction: string;
  roleId: number;
  roleName: string;
  permissionLabel: string;
  permissionDescription: string;
}

interface Message {
  id: number;
  text: string;
  sender: 'user' | 'agent';
  timestamp: Date;
  grantProposal?: GrantProposal | null;
  grantStatus?: 'pending' | 'confirmed' | 'error';
}

interface AgentConfig {
  name: string;
  isActive: boolean;
}

interface ChatBubbleProps {
  agentConfig: AgentConfig;
}

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    SpeechRecognition: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    webkitSpeechRecognition: any;
  }
}

export default function ChatBubble({ agentConfig }: ChatBubbleProps) {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recognitionRef = useRef<any>(null);

  const buildHistory = useCallback(
    (msgs: Message[]) =>
      msgs.slice(-20).map((m) => ({
        role: m.sender === 'user' ? ('user' as const) : ('model' as const),
        text: m.text,
      })),
    []
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!user) return;
    if (isOpen && messages.length === 0) {
      const firstName = user.fullName?.split(' ')[0] ?? '';
      setMessages([
        {
          id: 1,
          text: `¡Hola${firstName ? `, ${firstName}` : ''}! Soy ${agentConfig.name}. ¿En qué puedo ayudarte hoy?`,
          sender: 'agent',
          timestamp: new Date(),
        },
      ]);
    }
  }, [isOpen, messages.length, user, agentConfig.name]);

  useEffect(() => {
    if (isOpen) setUnreadCount(0);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  if (!user) return null;

  const sendMessage = async (text: string, currentMessages: Message[]) => {
    setIsLoading(true);
    try {
      const res = await fetch('/api/agent-config/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: text,
          history: buildHistory(currentMessages),
        }),
      });
      const data = await res.json();
      const reply = res.ok
        ? (data.reply ?? 'Lo siento, no pude procesar tu mensaje.')
        : (data.error ?? 'Error de conexión con el agente.');

      setMessages((prev) => [
        ...prev,
        {
          id: prev.length + 1, text: reply, sender: 'agent', timestamp: new Date(),
          grantProposal: data.grantProposal ?? null,
          grantStatus: data.grantProposal ? 'pending' : undefined,
        },
      ]);

      if (!isOpen) setUnreadCount((n) => n + 1);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: prev.length + 1,
          text: 'No se pudo conectar con el agente. Intenta de nuevo.',
          sender: 'agent',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmGrant = async (messageId: number, proposal: GrantProposal) => {
    try {
      const res = await fetch('/api/agent-config/chat/confirm-grant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ permissionAction: proposal.permissionAction, roleId: proposal.roleId }),
      });
      setMessages((prev) => prev.map((m) =>
        m.id === messageId ? { ...m, grantStatus: res.ok ? 'confirmed' : 'error' } : m
      ));
    } catch {
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, grantStatus: 'error' } : m)));
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim() || isLoading) return;

    const text = inputText.trim();
    const userMsg: Message = {
      id: messages.length + 1,
      text,
      sender: 'user',
      timestamp: new Date(),
    };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInputText('');
    await sendMessage(text, updated);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e as any);
    }
  };

  const toggleRecording = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert('Tu navegador no soporta reconocimiento de voz. Usa Chrome o Edge.');
      return;
    }

    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
      return;
    }

    const recognition = new SR();
    recognition.lang = 'es-MX';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => setIsRecording(true);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript.trim();
      if (!transcript) return;

      const userMsg: Message = {
        id: Date.now(),
        text: transcript,
        sender: 'user',
        timestamp: new Date(),
      };

      setMessages((prev) => {
        const updated = [...prev, userMsg];
        sendMessage(transcript, updated);
        return updated;
      });

      setInputText('');
    };

    recognition.onerror = () => setIsRecording(false);
    recognition.onend = () => setIsRecording(false);

    recognitionRef.current = recognition;
    recognition.start();
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 text-white shadow-xl hover:shadow-2xl hover:scale-110 transition-all duration-300 flex items-center justify-center group"
        title={`Chat con ${agentConfig.name}`}
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <Bot className="w-7 h-7 group-hover:animate-bounce" />
        )}
        {!isOpen && unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-28 right-6 z-50 w-96 h-[580px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-5 duration-300 border border-gray-100">
          {/* Header */}
          <div className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white p-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">{agentConfig.name}</h3>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                  <p className="text-xs text-violet-100">Asistente Virtual · Gemini AI</p>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white/20"
            >
              <Minimize2 className="w-4 h-4" />
            </Button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {message.sender === 'agent' && (
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center shrink-0 mr-2 mt-0.5">
                    <Bot className="w-3 h-3 text-white" />
                  </div>
                )}
                <div
                  className={`max-w-[72%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    message.sender === 'user'
                      ? 'bg-indigo-600 text-white rounded-br-sm'
                      : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm shadow-sm'
                  }`}
                >
                  {message.sender === 'agent' ? (
                    <div className="prose prose-sm max-w-none prose-p:my-0.5 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-strong:text-gray-900 prose-headings:text-gray-900">
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
                          ul: ({ children }) => <ul className="list-disc pl-4 my-1 space-y-0.5">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal pl-4 my-1 space-y-0.5">{children}</ol>,
                          li: ({ children }) => <li className="leading-snug">{children}</li>,
                          strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
                          em: ({ children }) => <em className="italic">{children}</em>,
                          code: ({ children }) => <code className="bg-gray-100 px-1 rounded text-xs font-mono">{children}</code>,
                          h1: ({ children }) => <h1 className="text-base font-bold mt-2 mb-1">{children}</h1>,
                          h2: ({ children }) => <h2 className="text-sm font-bold mt-2 mb-1">{children}</h2>,
                          h3: ({ children }) => <h3 className="text-sm font-semibold mt-1 mb-0.5">{children}</h3>,
                        }}
                      >
                        {message.text}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap">{message.text}</p>
                  )}
                  {message.grantProposal && (
                    <div className="mt-2 border border-indigo-200 bg-indigo-50 rounded-lg p-3">
                      <p className="text-xs font-semibold text-indigo-900 mb-1">Propuesta de asignación</p>
                      <p className="text-sm text-gray-800"><b>{message.grantProposal.permissionLabel}</b> → rol <b>{message.grantProposal.roleName}</b></p>
                      <p className="text-xs text-gray-600 mt-1">{message.grantProposal.permissionDescription}</p>
                      {message.grantStatus === 'pending' && (
                        <Button
                          size="sm"
                          className="mt-2"
                          onClick={() => confirmGrant(message.id, message.grantProposal!)}
                        >
                          Confirmar asignación
                        </Button>
                      )}
                      {message.grantStatus === 'confirmed' && (
                        <p className="text-xs text-green-700 mt-2 font-medium">✓ Permiso asignado</p>
                      )}
                      {message.grantStatus === 'error' && (
                        <p className="text-xs text-red-700 mt-2 font-medium">No se pudo asignar — revisa que tengas autorización.</p>
                      )}
                    </div>
                  )}
                  <span
                    className={`text-xs mt-1 block ${
                      message.sender === 'user' ? 'text-indigo-200' : 'text-gray-400'
                    }`}
                  >
                    {message.timestamp.toLocaleTimeString('es-MX', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center shrink-0 mr-2 mt-0.5">
                  <Bot className="w-3 h-3 text-white" />
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                  <div className="flex gap-1 items-center">
                    <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                    <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                    <span className="text-xs text-gray-400 ml-1">Pensando...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSend} className="border-t p-3 bg-white shrink-0">
            {isRecording && (
              <div className="flex items-center gap-2 mb-2 px-1">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-xs text-red-500 font-medium">Grabando... habla ahora</span>
              </div>
            )}
            <div className="flex gap-2 items-end">
              <textarea
                ref={inputRef}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Escribe tu mensaje..."
                rows={2}
                className="flex-1 resize-none rounded-xl border border-gray-200 p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
                disabled={isLoading}
              />
              <div className="flex flex-col gap-1.5">
                <button
                  type="button"
                  onClick={toggleRecording}
                  title={isRecording ? 'Detener grabación' : 'Grabar mensaje de voz'}
                  className={`h-10 w-10 rounded-xl flex items-center justify-center transition-all border ${
                    isRecording
                      ? 'bg-red-500 border-red-500 text-white animate-pulse'
                      : 'bg-white border-gray-200 text-gray-500 hover:border-violet-400 hover:text-violet-600'
                  }`}
                >
                  {isRecording ? (
                    <MicOff className="w-4 h-4" />
                  ) : (
                    <Mic className="w-4 h-4" />
                  )}
                </button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={!inputText.trim() || isLoading}
                  className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white rounded-xl h-10 w-10 p-0"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-1.5 px-1">Shift+Enter para nueva línea</p>
          </form>
        </div>
      )}
    </>
  );
}
